import { gql } from 'graphql-request'
import { client } from '@/proxy/client'
import { useQuery, type UseQueryHookOptions } from '@/proxy/react-query'
import {
  BackendSystemAlarm,
  type BackendSystemHealthCheckData,
} from '@/proxy/performance/backend/types'

export const SYSTEM_HEALTH_CHECK = gql`
  query sellerStatus {
    seller {
      systemHealth {
        lastUpdatedAt
        alarms {
          alarmName
          state
          metricName
          lastStateChangeAt
        }
      }
    }
  }
`

const BACKEND_SYSTEM_HEALTH_KEY = 'useBackendSystemHealth'

enum BackendSystemUnhealthyAlarms {
  WebSocketHealthAlarm = 'order-stream-iterator-age',
}

const isSystemHealthy = ({
  receivedSystemAlarms,
  unhealthySystemAlarms,
}: {
  unhealthySystemAlarms: BackendSystemUnhealthyAlarms[]
  receivedSystemAlarms: BackendSystemAlarm[]
}): boolean => {
  const hasUnhealthySystemAlarm = receivedSystemAlarms?.some(alarm =>
    unhealthySystemAlarms.includes(
      alarm.alarmName as BackendSystemUnhealthyAlarms
    )
  )

  return !hasUnhealthySystemAlarm
}

type UseBackendSystemHealthParams = Pick<
  UseQueryHookOptions<BackendSystemHealthCheckData>,
  'refetchInterval'
> & {
  skip: boolean
}

export const useBackendSystemHealth = (
  params: UseBackendSystemHealthParams
) => {
  const { skip, refetchInterval } = params
  const query = useQuery<BackendSystemHealthCheckData>({
    queryKey: [BACKEND_SYSTEM_HEALTH_KEY],
    queryFn: () => client.request(SYSTEM_HEALTH_CHECK),
    enabled: !skip,
    refetchInterval,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  })

  const systemHealthAlarms = query.data?.seller?.systemHealth?.alarms

  const isWebSocketSystemHealthy = isSystemHealthy({
    unhealthySystemAlarms: [BackendSystemUnhealthyAlarms.WebSocketHealthAlarm],
    receivedSystemAlarms: systemHealthAlarms ?? [],
  })

  return {
    isWebSocketSystemHealthy,
  }
}
